import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Button, Card, CardContent, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import { take } from "lodash";
import { Link } from "react-router-dom";
import { useRegions } from "../../admin/dictionaries/useRegions";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import { CardMediaImg } from "../CardImage"; // Import useRegions
import { CardLinkArea } from "../CardLinkArea";
import { packageImageSx } from "../packageMedia";
import TopicsCarousel from "./TopicsCarousel";

export function PackageRegions() {
  const { regions, isLoading } = useRegions(); // Use the hook
  const { limit, loadMore } = useLimit();

  if (isLoading) {
    return <LinearProgress data-testid="loading-indicator" />;
  }

  return (
    <Box sx={{ animation: "fadeIn 0.4s ease-out", pb: 8 }}>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
            }}
          >
            Gotowe Pakiety Wycieczkowe
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            Wybierz region, z którego chciałbyś zacząć odkrywać najciekawsze gotowe pakiety wyjazdowe dla swoich
            klientów.
          </Typography>
        </Box>
        <Button
          component={Link}
          to="filter"
          color="inherit"
          endIcon={<NavigateNextIcon fontSize="small" />}
          sx={{ color: "text.secondary", textTransform: "none", mb: 0.5, fontWeight: 500 }}
        >
          Zobacz wszystkie pakiety
        </Button>
      </Box>
      <TopicsCarousel />

      {/* Grid Kart - Elastyczny format z ekranu Pakietów */}
      <InfiniteScroll
        dataLength={limit < regions.length ? limit : regions.length}
        hasMore={regions.length > limit}
        fetchData={loadMore}
        scrollableTarget={null}
      >
        <Box
          sx={{
            display: "grid",
            // Elastyczna siatka: karty mają min 320px szerokości i dopasowują się do ekranu
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 3,
          }}
        >
          {take(regions, limit).map((region) => (
            <Card
              key={region.id.id}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                transition: "0.3s",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardLinkArea
                sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                to={`filter?region=${encodeURIComponent(region.id.id)}`}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                <CardMediaImg component="img" imageUrl={region.image?.thumbnail?.url} sx={packageImageSx} />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    {region.name} {/* Use region.name */}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 2,
                    }}
                  >
                    {region.description}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{
                      flexWrap: "wrap",
                    }}
                  >
                    {region.countries.map((country, idx) => (
                      <Chip
                        key={idx}
                        label={country}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ mb: 1, mr: 1 }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </CardLinkArea>
            </Card>
          ))}
        </Box>
      </InfiniteScroll>
    </Box>
  );
}
